import { Injectable } from "@angular/core";
import { NavigationEnd, NavigationError, Router } from "@angular/router";
import rg4js from "raygun4js";
import { environment } from "../../../environments/environment";
import { EnvironmentQuery } from "../state/environment.query";
import { EnvironmentState } from "../state/environment.state";
import { LocationStateQuery } from "../state/location-state.query";
import { UtilityService } from "./utility.service";

@Injectable({
	providedIn: "root",
})
export class RaygunService {
	constructor(
		private locationStateQuery: LocationStateQuery,
		private environmentQuery: EnvironmentQuery,
		private router: Router,
		private utilityService: UtilityService,
	) {}

	private activated: boolean | undefined;
	public activate = async () => {
		if (this.activated) {
			return;
		}

		this.activated = true;

		this.includeRideOpsKey();
		this.trackActiveOperator();
		this.trackNavigationChanges();
		this.configureForActiveEnvironment();
		this.addLocalStorageToPayload();
	};

	private addLocalStorageToPayload = () => {
		rg4js("onBeforeSend", (payload) => {
			// Adds Custom Error data for specific JSON.parse error #887
			if (payload?.Details?.Error?.Message?.startsWith('"undefined" is not valid JSON')) {
				payload.Details.UserCustomData = {
					...payload.Details.UserCustomData,
					localStorage: this.localStorageData(),
				};
			}

			return payload;
		});
	};

	private localStorageData = (): Record<string, string> => {
		const localStorageData: Record<string, string> = {};
		try {
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				localStorageData[key] = localStorage.getItem(key);
			}
		} catch (err) {
			localStorageData["error"] = "Error accessing localStorage: " + err;
		}

		return localStorageData;
	};

	public send = (error: Error, customData: object) => {
		rg4js("send", {
			error: error,
			customData: customData,
		});
	};

	private includeRideOpsKey = () => {
		const getApiKey = (): { rideOpsKey: string } => {
			try {
				const store = JSON.parse(localStorage.getItem("location-state@store"));
				return { rideOpsKey: this.utilityService.maskString(store.apiKey, "*", 3, 3) };
			} catch {
				return { rideOpsKey: null };
			}
		};

		rg4js("withCustomData", getApiKey);
	};

	private trackActiveOperator = () =>
		this.locationStateQuery.selectOperator$.subscribe((operator) => {
			if (operator == undefined) {
				rg4js("setUser", {
					identifier: null,
					isAnonymous: true,
				});
			} else {
				rg4js("setUser", {
					identifier: operator.id,
					isAnonymous: false,
					fullName: operator.name,
				});
			}
		});

	private trackNavigationChanges = () =>
		this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				rg4js("trackEvent", {
					type: "pageView",
					path: event.url,
				});
			} else if (event instanceof NavigationError) {
				rg4js("send", { error: event.error });
			}
		});

	private configureForActiveEnvironment = () =>
		this.environmentQuery.environment$.subscribe((backendEnvironment) => {
			const isDevelopment = backendEnvironment.environment == EnvironmentState.Development;

			rg4js("withTags", [backendEnvironment.name, environment.buildNumber]);
			rg4js("options", { disableErrorTracking: isDevelopment });
		});
}
